import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 30000,
});

// Add JWT to every request automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("valoriza_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If 401 is received, clear session and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("valoriza_token");
      localStorage.removeItem("valoriza_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const apiLogin = (email, password) =>
  api.post('/auth/login', { email, password }).then(r => r.data);

export const apiMsalLogin = (idToken) =>
  api.post('/auth/msal-login', { token: idToken }).then(r => r.data);

export const apiAccessRequest = (body) =>
  api.post('/auth/access-request', body).then(r => r.data);

export const apiGetAccessRequest = (requestId, token) =>
  api.get(`/auth/access-request/${requestId}`, { params: token ? { token } : {} }).then(r => r.data);

export const apiListAccessRequests = (status) =>
  api.get('/auth/access-requests', { params: status ? { status } : {} }).then(r => r.data);

export const apiRejectAccessRequest = (requestId, token = null, reason = '') =>
  api.post(`/auth/access-request/${requestId}/reject`, { token, reason }).then(r => r.data);

export const apiApproveAccessRequest = (requestId, sedes, token = null) =>
  api.post(`/auth/access-request/${requestId}/approve`, { sedes, token }).then(r => r.data);

export const fetchKPIs = (params = {}) => api.get('/kpis/summary', { params }).then(r => r.data);
export const fetchTrend = (params = {}) => api.get('/kpis/trend', { params }).then(r => r.data);
export const fetchKpisPorDelegacion = (params = {}) => api.get('/kpis/por-delegacion', { params }).then(r => r.data);
export const fetchPlanIncKpis = (params = {}) => api.get('/kpis/planinc', { params }).then(r => r.data);
export const fetchCpcKpis = (params = {}) => api.get('/kpis/cpc', { params }).then(r => r.data);
export const fetchSemaforo = (params = {}) => api.get('/kpis/semaforo', { params }).then(r => r.data);
export const fetchTrendMulti = (params = {}) => api.get('/kpis/trend-multi', { params }).then(r => r.data);

// ─── Liderazgo ────────────────────────────────────────────────────────────────
export const fetchLiderazgo = (params = {}) => api.get('/liderazgo', { params }).then(r => r.data);
export const fetchLiderazgoResumen = (params = {}) => api.get('/liderazgo/resumen', { params }).then(r => r.data);
export const fetchLiderazgoTopPersonas = (params = {}) => api.get('/liderazgo/top-personas', { params }).then(r => r.data);
export const fetchLiderazgoPorActividad = (params = {}) => api.get('/liderazgo/por-actividad', { params }).then(r => r.data);
export const fetchLiderazgoPorSede = (params = {}) => api.get('/liderazgo/por-sede', { params }).then(r => r.data);
export const fetchLiderazgoHeatmap = (params = {}) => api.get('/liderazgo/heatmap', { params }).then(r => r.data);

// ─── OPS ──────────────────────────────────────────────────────────────────────
export const fetchOPS = (params = {}) => api.get('/ops', { params }).then(r => r.data);
export const fetchOPSResumen = (params = {}) => api.get('/ops/resumen', { params }).then(r => r.data);
export const fetchOPSIncumplimientoPorSede = (params = {}) => api.get('/ops/incumplimiento-por-sede', { params }).then(r => r.data);
export const fetchOPSPorTipo = (params = {}) => api.get('/ops/por-tipo', { params }).then(r => r.data);
export const fetchOPSTopPersonas = (params = {}) => api.get('/ops/top-personas', { params }).then(r => r.data);
export const fetchOPSEspaciosConfinados = (params = {}) => api.get('/ops/espacios-confinados-breakdown', { params }).then(r => r.data);

// ─── Near Misses ──────────────────────────────────────────────────────────────
export const fetchNearMisses = (params = {}) => api.get('/near-misses', { params }).then(r => r.data);
export const fetchNMResumen = (params = {}) => api.get('/near-misses/resumen', { params }).then(r => r.data);
export const fetchNMPorCategoria = (params = {}) => api.get('/near-misses/por-categoria', { params }).then(r => r.data);
export const fetchNMTimeline = (params = {}) => api.get('/near-misses/timeline', { params }).then(r => r.data);
export const fetchNMPorGravedad = (params = {}) => api.get('/near-misses/por-gravedad', { params }).then(r => r.data);

// ─── Filters ──────────────────────────────────────────────────────────────────
export const fetchSedes    = () => api.get('/filters/sedes').then(r => r.data);
export const fetchNegocios = () => api.get('/filters/negocios').then(r => r.data);
export const fetchCategorias = () => api.get('/filters/categorias').then(r => r.data);
export const fetchGerentes = () => api.get('/filters/gerentes').then(r => r.data);
// Aliases for ModuleFilterBar usage
export const fetchFilterSedes   = fetchSedes;
export const fetchFilterNegocios = fetchNegocios;

// ─── Admin ────────────────────────────────────────────────────────────────────
export const fetchAdminUsers = () => api.get('/admin/users').then(r => r.data);
export const createAdminUser = (user) => api.post('/admin/users', user).then(r => r.data);
export const updateAdminUser = (id, updates) => api.put(`/admin/users/${id}`, updates).then(r => r.data);
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const fetchAuditLog = (params = {}) => api.get('/admin/audit-log', { params }).then(r => r.data);


// ─── Centros ──────────────────────────────────────────────────────────────────
export const fetchCentros = (params = {}) => api.get('/centros', { params }).then(r => r.data);
export const fetchCentrosCatalog = (search, includeArchived) => api.get('/admin/centros-catalog', { params: { ...(search ? { search } : {}), ...(includeArchived ? { include_archived: true } : {}) } }).then(r => r.data);
export const archivarCentro = (codigo, reason) => api.post(`/admin/centros-catalog/${codigo}/archivar`, { reason }).then(r => r.data);
export const desarchivarCentro = (codigo) => api.post(`/admin/centros-catalog/${codigo}/desarchivar`).then(r => r.data);
export const fetchJefeBySede = (sede) => api.get(`/admin/jefe-by-sede/${sede}`).then(r => r.data);

// ─── System ───────────────────────────────────────────────────────────────────
export const fetchSystemStatus = () => api.get('/system/status').then(r => r.data);
export const fetchSyncStatus   = () => api.get('/sync/status').then(r => r.data);
export const triggerSync       = () => api.post('/sync/trigger').then(r => r.data);
export const fetchArcGISToken = () => api.get('/arcgis/token').then(r => r.data);
export const fetchCacheStats = () => api.get('/admin/cache-stats').then(r => r.data);
export const invalidateCache = () => api.post('/admin/cache-invalidate').then(r => r.data);

// ─── Admin RLS ────────────────────────────────────────────────────────────────
export const fetchRLSRules = (search) => api.get('/admin/rls-rules', { params: search ? { search } : {} }).then(r => r.data);
export const updateRLSRule = (email, sedes) => api.put(`/admin/rls-rules/${encodeURIComponent(email)}`, { sedes }).then(r => r.data);
export const deleteRLSRule = (email) => api.delete(`/admin/rls-rules/${encodeURIComponent(email)}`).then(r => r.data);

// ─── Siniestralidad ───────────────────────────────────────────────────────────
export const fetchSiniestStatus = () => api.get('/siniestralidad/status').then(r => r.data);
export const fetchSiniestKpis = (params = {}) => api.get('/siniestralidad/kpis', { params }).then(r => r.data);
export const fetchSiniestMensual = (params = {}) => api.get('/siniestralidad/mensual', { params }).then(r => r.data);
export const fetchSiniestComparativaAnual = () => api.get('/siniestralidad/comparativa-anual').then(r => r.data);
export const fetchSiniestPorZona = (params = {}) => api.get('/siniestralidad/por-zona', { params }).then(r => r.data);
export const fetchSiniestPropios = (params = {}) => api.get('/siniestralidad/propios', { params }).then(r => r.data);
export const fetchSiniestPorTipo = (params = {}) => api.get('/siniestralidad/charts/por-tipo', { params }).then(r => r.data);
export const fetchSiniestPorRiesgo = (params = {}) => api.get('/siniestralidad/charts/por-riesgo', { params }).then(r => r.data);
export const fetchSiniestPorLugar = (params = {}) => api.get('/siniestralidad/charts/por-lugar', { params }).then(r => r.data);
export const fetchSiniestFilterGerentes = () => api.get('/siniestralidad/filters/gerentes').then(r => r.data);
export const fetchSiniestFilterZonas = () => api.get('/siniestralidad/filters/zonas').then(r => r.data);
export const importSiniestralidad = (payload) => api.post('/siniestralidad/import', payload, {
  timeout: 120000,
}).then(r => r.data);
export const deleteSiniestPorAno = (ano) => api.delete(`/siniestralidad/datos/${ano}`).then(r => r.data);
export const exportSiniestPDF = (params = {}) => api.get('/siniestralidad/export/pdf', {
  params, responseType: 'blob',
}).then(r => r.data);
export const fetchCorrelacion = (year) => api.get('/correlacion/centros', { params: year ? { year } : {} }).then(r => r.data);
export const fetchCorrelacionCompare = (yearA, yearB) => api.get('/correlacion/compare', { params: { year_a: yearA, year_b: yearB } }).then(r => r.data);
export const exportCorrelacionExcel = (year) => api.get('/export/excel/correlacion', { params: year ? { year } : {}, responseType: 'blob' }).then(r => r.data);
export const exportCorrelacionPDF = (year) => api.get('/export/pdf/correlacion', { params: year ? { year } : {}, responseType: 'blob' }).then(r => r.data);
export const fetchSiniestAlertas = () => api.get('/siniestralidad/alertas').then(r => r.data);
export const marcarAlertasLeidas = () => api.post('/siniestralidad/alertas/marcar-leidas').then(r => r.data);
export const fetchAlertasConfig = () => api.get('/siniestralidad/alertas/config').then(r => r.data);
export const saveAlertasConfig = (config) => api.put('/siniestralidad/alertas/config', config).then(r => r.data);
export const fetchSiniestCuarentena = () => api.get('/siniestralidad/cuarentena').then(r => r.data);
export const corregirSiniestCuarentena = (qid, updates) => api.put(`/siniestralidad/cuarentena/${qid}`, updates).then(r => r.data);
export const descartarSiniestCuarentena = (qid) => api.delete(`/siniestralidad/cuarentena/${qid}`).then(r => r.data);

// ─── Cumplimiento ─────────────────────────────────────────────────────────────
export const fetchCumplimiento = (params = {}) => api.get('/cumplimiento/resumen', { params }).then(r => r.data);

// ─── Attachments (Photos) ─────────────────────────────────────────────────────
// API returns {objectId, layer, count, attachments: [...]} — extract the array
export const fetchAttachments = (module, objectid) =>
  api.get(`/attachments/${module}/${objectid}`).then(r => r.data.attachments || []);

// ─── Map ──────────────────────────────────────────────────────────────────────
export const fetchMapPoints = (layer, limit = 2000, params = {}) =>
  api.get(`/map-points/${layer}`, { params: { limit, ...params } }).then(r => r.data);
export const fetchMapPointsCounts = (params = {}) =>
  api.get('/map-points-counts', { params }).then(r => r.data);

// ─── Dynamic module filters ───────────────────────────────────────────────────
export const fetchFilterTipoops       = () => api.get('/filters/tipoops').then(r => r.data);
export const fetchFilterActividades   = () => api.get('/filters/actividades').then(r => r.data);
export const fetchFilterGravedades    = () => api.get('/filters/gravedades').then(r => r.data);
export const fetchFilterEstados       = () => api.get('/filters/estados').then(r => r.data);
export const fetchFilterClasificaciones = () => api.get('/filters/clasificaciones').then(r => r.data);
export const fetchFilterNMCentros     = () => api.get('/near-misses/filter-centros').then(r => r.data);
export const fetchFilterNMResponsables = () => api.get('/near-misses/filter-responsables').then(r => r.data);
export const fetchNMResumenResponsables = (params) => api.get('/near-misses/resumen-responsables', { params }).then(r => r.data);

// ─── Admin: Sin Sede ──────────────────────────────────────────────────────────
export const fetchSinSede = () => api.get('/admin/sin-sede').then(r => r.data);
export const fetchNmHuerfanos = () => api.get('/admin/nm-huerfanos').then(r => r.data);
export const fetchDiasSinIncidentes = () => api.get('/dashboard/dias-sin-incidentes').then(r => r.data);

// NM responsable "Otro": cola de resolución manual (31/08 follow-up)
export const fetchNmResponsableOtro = () => api.get('/admin/nm-responsable-otro').then(r => r.data);
export const asignarResponsableNM = (objectid, payload) => api.post(`/admin/nm-responsable-otro/${objectid}/asignar`, payload).then(r => r.data);
export const fetchNmResponsableOverrides = () => api.get('/admin/nm-responsable-overrides').then(r => r.data);
export const editNmResponsableOverride = (objectid, payload) => api.put(`/admin/nm-responsable-overrides/${objectid}`, payload).then(r => r.data);
export const deleteNmResponsableOverride = (objectid) => api.delete(`/admin/nm-responsable-overrides/${objectid}`).then(r => r.data);
export const fetchPersonasCatalog = () => api.get('/admin/personas-catalog').then(r => r.data);
export const fetchSedesCatalogo = () => api.get('/admin/sedes-catalogo').then(r => r.data);
export const asignarSede = (payload) => api.post('/admin/asignar-sede', payload).then(r => r.data);

// Mapeo de sedes texto → código
export const fetchSedesTextoSinCodigo = () => api.get('/admin/sedes-texto-sin-codigo').then(r => r.data);
export const fetchSedesTextoMapping = () => api.get('/admin/sedes-texto-mapping').then(r => r.data);
export const saveSedeTextoMapping = (payload) => api.post('/admin/sedes-texto-mapping', payload).then(r => r.data);
export const bulkSaveSedeTextoMapping = (items) => api.post('/admin/sedes-texto-mapping-bulk', items).then(r => r.data);
export const deleteSedeTextoMapping = (texto) => api.delete(`/admin/sedes-texto-mapping/${encodeURIComponent(texto)}`).then(r => r.data);
export const fetchSedesFuzzySuggest = (texto) => api.get(`/admin/sedes-fuzzy-suggest?texto=${encodeURIComponent(texto)}`).then(r => r.data);

// Código→Código: alias manual origen→destino (31/08 #7)
export const fetchSedeCodeCorrections = () => api.get('/admin/sede-code-corrections').then(r => r.data);
export const createSedeCodeCorrection = (payload) => api.post('/admin/sede-code-corrections', payload).then(r => r.data);
export const deleteSedeCodeCorrection = (aliasCode) => api.delete(`/admin/sede-code-corrections/${encodeURIComponent(aliasCode)}`).then(r => r.data);
export const suggestSedeCodePad = (code) => api.get(`/admin/sede-code-suggest?code=${encodeURIComponent(code)}`).then(r => r.data);

// NM sin sede: asignación real (31/08 #10)
export const asignarSedeNM = (objectid, payload) => api.post(`/admin/nm-huerfanos/${objectid}/asignar`, payload).then(r => r.data);

// ─── Admin: Data Quality ──────────────────────────────────────────────────────
export const fetchDataQuality = () => api.get('/admin/data-quality').then(r => r.data);
export const triggerNormalize = () => api.post('/sync/normalize').then(r => r.data);


// ─── D3 + D4 — Centros activos / sin actividad ────────────────────────────────
export const fetchCentrosActividad = (params = {}) =>
  api.get('/kpis/centros-actividad', { params }).then(r => r.data);


// ─── D1 + D5 + D7 — Cumplimiento de objetivos por responsable ─────────────────
export const fetchCumplimientoResponsables = (params = {}) =>
  api.get('/kpis/cumplimiento-responsables', { params }).then(r => r.data);

// ─── D2 — Comparativa periodo actual vs mismo periodo año anterior ────────────
export const fetchComparativaAnual = (params = {}) =>
  api.get('/kpis/comparativa-anual', { params }).then(r => r.data);

// ─── Mis Centros Resumen (ops users with RLS sedes) ───────────────────────────
export const fetchMisCentrosResumen = () =>
  api.get('/kpis/mis-centros-resumen').then(r => r.data);

// ─── Objetivos CRUD + recordatorio email ──────────────────────────────────────
export const fetchObjetivos = () =>
  api.get('/objetivos').then(r => r.data);

export const upsertObjetivo = (payload) =>
  api.put('/objetivos', payload).then(r => r.data);

export const sendCumplimientoReminder = (responsable, params = {}) =>
  api.post('/kpis/cumplimiento-responsables/email', null, { params: { responsable, ...params } }).then(r => r.data);
